import { Hidden, Link } from '@mui/material'
import cn from 'classnames'
import moment from 'moment'
import { GetStaticProps, NextPage } from 'next'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { useRouter } from 'next/router'
import React, { useMemo, useState } from 'react'
//import { ContentPagination } from 'src/components/ContentPagination/ContentPagination'
import { ContentWriter } from 'src/components/ContentWriter/ContentWriter'
import { Footer } from 'src/components/Footer/Footer'
import { HtmlContentViewer } from 'src/components/global/HtmlContentViewer/HtmlContentViewer'
import MainContainer from 'src/components/global/MainContainer/MainContainer'
//import { PremiumModal } from 'src/components/global/PremiumModal'
//import { SearchField } from 'src/components/global/SearchField'
// import { Sidebar } from 'src/components/global/Sidebar'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
import SkeletonLoading from 'src/components/SkeletonLoading/SkeletonLoading'
//import { ReCaptcha } from 'src/components/ReCaptcha/ReCaptcha'
import { THEME } from 'src/config/config'
import { apolloSdk } from 'src/graphql/apolloSdk'
import { IBlogTaxonomyFullDataFragment, IEnum_Content_Contenttype } from 'src/graphql/generated/hooks'
import { useAuthenticated } from 'src/hooks/useAuthenticated'
import { useDefaultLogo } from 'src/hooks/useDefaultLogo'
import { Meta } from 'src/layout/Meta'
import { Main } from 'src/templates/Main'
import { FilterOption, TaxonomyType } from 'src/types'
import { normalizeWritersList } from 'src/utils/normalizeContentWriters'
import { removeTags } from 'src/utils/removeTags'
import { withRetries } from 'src/utils/retryHelper'
import { getBlogsFilters } from 'src/utils/TopicHelpers'

import styles from './blogs.module.scss'

type Props = {
  slug: string
  blogTaxonomy: IBlogTaxonomyFullDataFragment
  filters: FilterOption[]
}

// Dynamically import heavy components

const Sidebar = dynamic(() => import('src/components/global/Sidebar').then((mod) => mod.Sidebar), {
  ssr: false,
  loading: () => <SkeletonLoading className="bg-navy" />,
})
const PremiumModal = dynamic(() => import('src/components/global/PremiumModal').then((mod) => mod.PremiumModal), {
  ssr: false,
  loading: () => <SkeletonLoading className="bg-navy" />,
})
const SearchField = dynamic(() => import('src/components/global/SearchField').then((mod) => mod.SearchField), {
  ssr: false,
  loading: () => <SkeletonLoading className="bg-navy" />,
})
const ReCaptcha = dynamic(() => import('src/components/ReCaptcha/ReCaptcha').then((mod) => mod.ReCaptcha), {
  ssr: false,
  loading: () => <SkeletonLoading className="bg-navy" />,
})
const ContentPagination = dynamic(
  () => import('src/components/ContentPagination/ContentPagination').then((mod) => mod.ContentPagination),
  {
    ssr: false,
    loading: () => <SkeletonLoading className="bg-navy" />,
  },
)

const Blog: NextPage<Props> = ({ blogTaxonomy, filters }: Props) => {
  const router = useRouter()
  const [showModal, setShowModal] = useState(false)
  const { isVendor, status } = useAuthenticated()
  // const filtersDisabled = useMemo(() => !isPremium, [isPremium])
  const title = blogTaxonomy?.name || ''
  const description = removeTags(blogTaxonomy?.description || '')
  const formatedDate = moment(blogTaxonomy?.publishedAt).format('ddd, MMMM DD')
  const blogTaxonomyLogo = useDefaultLogo()
  const slug = blogTaxonomy?.slug

  // Get search keyword from query parameters for highlighting (reactive to router changes)
  const searchKeyword = useMemo(() => {
    if (!router.isReady) return undefined
    const keyword = router.query.keyword
    if (!keyword) return undefined

    let keywordStr: string
    if (Array.isArray(keyword)) {
      keywordStr = keyword[0] || ''
    } else {
      keywordStr = keyword as string
    }

    // Decode URI component in case it's URL encoded (handles + as space, %20, etc.)
    try {
      return decodeURIComponent(keywordStr)
    } catch {
      // If decoding fails, return as is
      return keywordStr || undefined
    }
  }, [router.isReady, router.query.keyword])

  const latestContent = blogTaxonomy?.latestContent || null
  const latestContentImage = useDefaultLogo(
    latestContent?.defaultImage && latestContent?.defaultImage.length > 0 ? latestContent?.defaultImage[0]?.url : null,
  )
  const latestContentSlug = latestContent?.slug

  const meta = (
    <Meta
      title={title}
      description={description}
      url={`/blogs/${slug}/`}
      image={blogTaxonomyLogo}
      type="article"
      article={{
        authors: ['Nicola (Nikki) Shaver, Jeroen Plink'],
        section: 'Blogs',
        publishedTime: formatedDate,
        modifiedTime: formatedDate,
      }}
    />
  )
  return (
    <ReCaptcha>
      <Main meta={meta}>
        <PremiumModal origin={`/blogs/${slug}/`} isOpen={showModal} onClose={() => setShowModal(false)} />
        <MainContainer id="mainContainer">
          <div className="bg-navy w-full">
            <h1 className="font-secondary font-bold text-[40px] leading-[48px] text-white text-center mt-[58px]">
              {title}
            </h1>
            <div className="flex flex-row !max-w-[1100px] w-full mx-auto pb-[28px]">
              <h2 className="font-secondary font-bold text-[18px] leading-[32px] text-turquoise text-center mt-[10px] px-5 md:px-0">
                {description}
              </h2>
            </div>
          </div>
          {latestContent && (
            <div className="bg-clouds lg:min-h-[357px] w-full py-[30px] px-5">
              <Hidden mdDown>
                <div className="font-secondary font-medium text-[14px] leading-[21px] text-darkgray !max-w-[1100px] mx-auto">
                  FEATURED ARTICLE
                </div>
              </Hidden>
              <div className="flex flex-row !max-w-[1100px] w-full mx-auto pt-[28px]">
                <Hidden mdDown>
                  <div className="min-w-[240px] mr-[40px]">
                    <Image
                      className={cn('mr-[1.6875rem]', styles.blogLogo)}
                      src={latestContentImage}
                      width={1000}
                      height={1000}
                      priority
                      alt=""
                    />
                  </div>
                </Hidden>
                <div>
                  <Link href={`/contents/${latestContentSlug}`}>
                    <span className="font-secondary font-bold text-[28px] leading-[34px] text-textdark underline">
                      {latestContent?.title}
                    </span>
                  </Link>
                  <div className="mt-[10px]">
                    {latestContent?.writer && <ContentWriter writer={normalizeWritersList(latestContent.writer)} />}
                  </div>
                  <HtmlContentViewer className="mt-[8px]" html={latestContent?.snippet || ''} />
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-row justify-center w-full md:pl-0 md:pr-0 pl-[20px] pr-[20px]">
            <div className="!max-w-[1100px] flex flex-row justify-between w-full mb-32">
              <Hidden mdDown>
                <div className="w-1/4 pr-10 flex flex-col">
                  <SearchField className="my-8" />
                  <Sidebar
                    filterOptions={
                      isVendor && status === 'authenticated'
                        ? filters.filter((filter) => filter.title !== TaxonomyType.vendorTopic)
                        : filters
                    }
                    // filtersDisabled={filtersDisabled}
                    defaultSelectFilterIdx={1}
                    onClickWhenDisabled={() => setShowModal(true)}
                  />
                </div>
              </Hidden>

              <ContentPagination
                filterProps={
                  isVendor && status === 'authenticated'
                    ? filters.filter((filter) => filter.title !== TaxonomyType.vendorTopic)
                    : filters
                }
                showDocumentType
                // filtersDisabled={filtersDisabled}
                contentType={IEnum_Content_Contenttype.Blog}
                onClickWhenDisabled={() => setShowModal(true)}
                preAppliedFilters={{ blogTaxonomyIds: blogTaxonomy.documentId! }}
                searchKeyword={searchKeyword}
              />
            </div>
          </div>
          <ThemeOnly theme="legaltech">
            <div className="mt-auto">
              <Footer />
            </div>
          </ThemeOnly>
        </MainContainer>
      </Main>
    </ReCaptcha>
  )
}

export default Blog

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  if (!params?.slug || THEME === 'ilta') return { revalidate: 60, notFound: true }

  const slug = params.slug as string
  try {
    const { blogTaxonomys } = await withRetries(() => apolloSdk.blogTaxonomyBySlug({ slug }))
    const blogTaxonomy = blogTaxonomys && blogTaxonomys.length > 0 ? blogTaxonomys[0] : null
    if (!blogTaxonomy) {
      return {
        revalidate: 60,
        notFound: true,
      }
    }

    const filters = await getBlogsFilters(blogTaxonomy)

    return {
      props: {
        slug,
        blogTaxonomy,
        filters,
      },
      revalidate: 60, // Only cache successful pages
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching blog taxonomy:', error)

    // Don't cache failure as 404
    return {
      notFound: true,
      revalidate: 5,
    }
  }
}

export async function getStaticPaths() {
  const { blogTaxonomys } = await apolloSdk.allBlogTaxonomySlugs()
  const paths = blogTaxonomys?.map((item) => ({ params: { slug: item?.slug || '' } })) || []

  return {
    paths,
    fallback: 'blocking',
  }
}
